import type { Enduser, FormResponseValue, FormResponseValueAnswer } from "@tellescope/types-models";

/**
 * `{{enduser.*}}` fields that are derived rather than read off the record. Resolved from the form's
 * in-progress responses when available, falling back to the enduser record.
 */
export const COMPUTED_ENDUSER_FIELDS = ["BMI", "Age"] as const;

export type ComputedEnduserField = (typeof COMPUTED_ENDUSER_FIELDS)[number];

const COMPUTED_ENDUSER_FIELD_NAMES: ReadonlySet<string> = new Set(COMPUTED_ENDUSER_FIELDS);

export const isComputedEnduserField = (field: string): field is ComputedEnduserField =>
  COMPUTED_ENDUSER_FIELD_NAMES.has(field);

/**
 * Aliases from the name a `{{enduser.*}}` token uses to the `intakeField` a form field is tagged
 * with, for the names where the two vocabularies disagree. Anything absent resolves under its own
 * name.
 */
const INTAKE_FIELD_ALIASES: Readonly<Record<string, string>> = {
  firstName: "fname",
  lastName: "lname",
};

/**
 * The value a form's own intake answers give for a plain `{{enduser.*}}` token, or `undefined` when
 * no answered field is tagged with the matching `intakeField`.
 *
 * This is what lets a form echo back a name or email the enduser just typed instead of the (possibly
 * empty or stale) value on their record, so it takes precedence over the record — the same ordering
 * {@link resolveComputedEnduserField} applies to `BMI`/`Age`.
 */
export const resolveIntakeFieldValue = (field: string, responses: FormResponseValue[]): string | undefined => {
  const intakeField = INTAKE_FIELD_ALIASES[field] ?? field;
  const answer = responses.find((r) => r.intakeField === intakeField && r.answer?.value)?.answer;
  if (!answer) return undefined;

  if (typeof answer.value === "string" || typeof answer.value === "number") return answer.value.toString();
  if (answer.type === "Address" && typeof answer.value === "object") return JSON.stringify(answer.value);
  return undefined;
};

/** Imperial BMI. Height in inches, weight in pounds. */
const bmiFor = (heightInches: number, weightPounds: number): number =>
  (703 * weightPounds) / (heightInches * heightInches);

const heightInInches = (answer: FormResponseValueAnswer | undefined): number | undefined => {
  if (answer?.type === "number" && typeof answer.value === "number") return answer.value;
  if (answer?.type === "Height" && typeof answer.value?.feet === "number") {
    return answer.value.feet * 12 + (answer.value.inches || 0);
  }
  return undefined;
};

const findAnswerByComputedKey = (
  responses: FormResponseValue[],
  computedValueKey: string
): FormResponseValueAnswer | undefined =>
  responses.find((r) => r.computedValueKey === computedValueKey && r.answer?.value)?.answer;

const bmiFromResponses = (responses: FormResponseValue[]): number | undefined => {
  const height = heightInInches(findAnswerByComputedKey(responses, "Height"));
  const weightAnswer = findAnswerByComputedKey(responses, "Weight");
  const weight =
    weightAnswer?.type === "number" && typeof weightAnswer.value === "number" ? weightAnswer.value : undefined;

  if (!height || !weight) return undefined;
  return bmiFor(height, weight);
};

const numericMeasurement = (measurement: Enduser["height"] | Enduser["weight"]): number | undefined => {
  const { value } = measurement ?? {};
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number.parseInt(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

const bmiFromEnduser = (enduser: Partial<Enduser> | null): number | undefined => {
  const height = numericMeasurement(enduser?.height);
  const weight = numericMeasurement(enduser?.weight);
  if (!height || !weight) return undefined;
  return bmiFor(height, weight);
};

/** Parses the `MM-DD-YYYY` form the enduser record and date answers use. */
const ageForDateOfBirth = (dateOfBirth: string): number | undefined => {
  const [month, day, year] = dateOfBirth.split("-").map((part) => Number.parseInt(part));
  if (month === undefined || day === undefined || year === undefined) return undefined;
  if (Number.isNaN(month) || Number.isNaN(day) || Number.isNaN(year)) return undefined;

  const today = new Date();
  const ageThisYear = today.getFullYear() - year;
  const hadBirthday = month < today.getMonth() + 1 || (month === today.getMonth() + 1 && day <= today.getDate());
  return hadBirthday ? ageThisYear : ageThisYear - 1;
};

const dateOfBirthFromResponses = (responses: FormResponseValue[]): string | undefined => {
  const response = responses.find((r) => r.intakeField === "dateOfBirth" || r.computedValueKey === "Date of Birth");
  return typeof response?.answer?.value === "string" ? response.answer.value : undefined;
};

/**
 * Resolves a computed `{{enduser.BMI}}` / `{{enduser.Age}}` token. Prefers the form's in-progress
 * responses so a value the enduser just entered wins over a stale record, matching legacy.
 * Returns `undefined` when the inputs aren't available, which the caller treats as a missing value.
 */
export const resolveComputedEnduserField = (
  field: ComputedEnduserField,
  { enduser, responses = [] }: { enduser: Partial<Enduser> | null; responses?: FormResponseValue[] }
): string | undefined => {
  if (field === "BMI") {
    const bmi = bmiFromResponses(responses) ?? bmiFromEnduser(enduser);
    return bmi === undefined ? undefined : bmi.toFixed(1);
  }

  const dateOfBirth = dateOfBirthFromResponses(responses) ?? enduser?.dateOfBirth;
  if (!dateOfBirth) return undefined;
  const age = ageForDateOfBirth(dateOfBirth);
  return age === undefined ? undefined : age.toString();
};
